/* ═══════════════════════════════════════════════════════════════════════════
   MOTOR PREKRŠAJA — pravila nad PERIODIMA rad ↔ odmor, ne po kalendarskom danu,
   stepenovanje po Zakonu (propisi.js) + režim "gradski" (TPS, ispravljen po čl. 5 st. 4 i čl. 8 st. 10).

   Ulaz (sve Date su "zidno vreme" bez zone — kao u celoj aplikaciji):
     drivers:    [{ id, name, company, cardExpiry, turistika }]  turistika: true -> AETR + R Srbije međugradski, false -> R Srbije gradski
                 (propisi po vozaču, rezimiVozaca); bez polja turistika važe zadati rezimi
     activities: [{ driverId, start, end, type: 'driving'|'work'|'availability'|'break/rest'|'unknown', note }]
     outs:       [{ driverId, entryDate, type }]   1 OUT početak, 2 OUT kraj, 3/4 trajekt
     from, to:   period izveštaja (dani, uključivo); podaci treba da počnu ranije (kontekst nedelje, 28 dana)
     rezimi:     ['AETR','RS_M','RS_G'];  skriveni: Set ključeva tipova koji se ne prikazuju
     posada:     [{ driverId, od, do }]  periodi u kojima je vozač vozio u posadi (udvojena posada, zapis tahografa sa kartice)
     mesta:      [{ driverId, t }]  uneta zemlja POČETKA radnog dana (zapis mesta sa kartice); bez zapisa za vozača pravilo se ne proverava
   Izlaz: niz prekršaja { driverId, vozac, kompanija, rezim, tip, tipNaziv, podtip, opis, od, do, vreme,
          trajanje, prekoracenje, manjak, nivo, clanTacka, kazna, napomena }
   ═══════════════════════════════════════════════════════════════════════════ */
import {
    H, MIN, LIMITI, NIVOI, tipZa,
    stepenDnevnaVoznja, stepenNedeljnaVoznja, stepenDvonedeljnaVoznja, stepenPeriodUpravljanja, stepenDnevniOdmor,
    stepenKasnoNedeljni, stepenGradskiNedeljni, stepenRadnoVreme, stepenNocniRad, stepenPauzaURadu, kaznaZa,
} from './propisi';

const DAN = 24 * H;
/* nepoznato (kartica nije u uređaju) = odmor, kao rupa u podacima i kao TPS; pravilo 'nepoznato' ga hvata posebno */
const jeOdmor = (v) => v === 'break/rest' || v === 'unknown';
const jeVoznja = (v) => v === 'driving';
const jeRad = (v) => v === 'work';
const NEMA_PODATAKA = 'Nema podataka na kartici.';

/* ── formatiranje ─────────────────────────────────────────────────────────── */
const p2 = (n) => String(n).padStart(2, '0');
export const fmtTrajanje = (ms) => `${p2(Math.floor(Math.max(0, ms) / H))}:${p2(Math.floor((Math.max(0, ms) % H) / MIN))}`;
const fmtDatum = (ms) => { const d = new Date(ms); return `${p2(d.getUTCDate())}.${p2(d.getUTCMonth() + 1)}.${d.getUTCFullYear()}`; };
const fmtDV = (ms) => { const d = new Date(ms); return `${fmtDatum(ms)} ${p2(d.getUTCHours())}:${p2(d.getUTCMinutes())}`; };

/* ── vreme ────────────────────────────────────────────────────────────────────
   Motor radi na ZIDNOJ vremenskoj liniji: zidno vreme podataka (UTC sa kartice, bez zone) kao Date.UTC ms — dan je uvek 24h.
   Ulaz su Date iz revivera (lokalni Date sa zidnim komponentama), pa bi razlika preko promene sata (poslednja nedelja
   oktobra/marta) bila ±1h: odmor 23.10 19:30 – 25.10 03:52 meren je 33:22 umesto 32:22 (Sakalawallii, Ćurdić 2026-11-02). */
const zid = (d) => { const x = new Date(d); return Date.UTC(x.getFullYear(), x.getMonth(), x.getDate(), x.getHours(), x.getMinutes(), x.getSeconds(), x.getMilliseconds()); };
const izZida = (ms) => { const x = new Date(ms); return new Date(x.getUTCFullYear(), x.getUTCMonth(), x.getUTCDate(), x.getUTCHours(), x.getUTCMinutes(), x.getUTCSeconds()); };
const ponoc = (ms) => Math.floor(ms / DAN) * DAN;
const sledeciDan = (ms) => ms + DAN;
const plusNedelja = (ms) => ms + 7 * DAN;
const danUNedelji = (ms) => new Date(ms).getUTCDay(); // 0 = nedelja
/** Ključ nedelje: ponedeljak 00:00, čl. 3 t. 27. */
const nedeljaKljuc = (ms) => ponoc(ms) - ((danUNedelji(ms) + 6) % 7) * DAN;
const preklop = (a1, a2, b1, b2) => Math.max(0, Math.min(a2, b2) - Math.max(a1, b1));
/* Vremena aktivnosti su UTC (kartica, tahograf), a noćno vreme (čl. 3 t. 40: 00–04) je po BEOGRADU: leti 22–02 UTC, zimi 23–03 UTC.
   Ranije se merilo u UTC pa je početak smene u 05:55 po Beogradu (03:55 UTC) bio "noćni rad" (kolege, Grikom 2026-10-28). */
const poslednjaNedelja = (godina, mesec) => { const d = Date.UTC(godina, mesec + 1, 0); return d - danUNedelji(d) * DAN; };
/** pomeraj Beograda (ms) za beogradski datum dana: od poslednje nedelje marta do poslednje nedelje oktobra letnje (+2h) */
const pomerajBeograda = (dan) => {
    const g = new Date(dan).getUTCFullYear();
    return dan > poslednjaNedelja(g, 2) && dan <= poslednjaNedelja(g, 9) ? 2 * H : H;
};
/** noćni prozor beogradskog dana (ponoć dana) u UTC vremenu podataka */
const nocniProzor = (dan, odSat, doSat) => { const p = pomerajBeograda(dan); return [dan + odSat * H - p, dan + doSat * H - p]; };
/** Koliko intervala pada u noćno vreme od..do sati po Beogradu, po svim danima koje dodiruje. */
const nocniPreklop = (od, doo, odSat, doSat) => {
    let ukupno = 0;
    for (let dan = ponoc(od); dan < doo + DAN; dan = sledeciDan(dan)) {
        const [a, b] = nocniProzor(dan, odSat, doSat);
        ukupno += preklop(od, doo, a, b);
    }
    return ukupno;
};

/* ── periodi rad / odmor ─────────────────────────────── */
export function napraviPeriode(aktivnosti, zatvaraPeriod) {
    const sirovi = [];
    let tek = null;
    for (const a of aktivnosti) {
        const od = a.od;
        const doo = a.do;
        if (!(doo > od)) continue;
        const tip = jeOdmor(a.v) ? 'ODMOR' : 'RAD';
        if (!tek || tek.tip !== tip || od > tek.do) {
            /* rupa u podacima = odmor (kartica nije bila u uređaju — ne može se računati kao rad) */
            if (tek && od > tek.do) {
                sirovi.push(tek);
                sirovi.push({ tip: 'ODMOR', od: tek.do, do: od, voznja: 0, rad: 0, raspolozivost: 0, rupa: true });
                tek = null;
            }
            if (tek) sirovi.push(tek);
            tek = { tip, od, do: doo, voznja: 0, rad: 0, raspolozivost: 0 };
        } else tek.do = Math.max(tek.do, doo);
        const t = doo - od;
        if (jeVoznja(a.v)) tek.voznja += t;
        else if (jeRad(a.v)) tek.rad += t;
        else if (a.v === 'availability') tek.raspolozivost += t;
    }
    if (tek) sirovi.push(tek);

    /* odmor kraći od praga ne zatvara radni period (pauza u toku rada); prvi/poslednji odmor je
       odsečen granicom podataka i ostaje kao nepotpun */
    const spojeni = [];
    sirovi.forEach((p, idx) => {
        const prethodni = spojeni[spojeni.length - 1];
        const trajanje = p.do - p.od;
        const ivica = idx === sirovi.length - 1 || idx === 0;
        if (p.tip === 'ODMOR' && ivica && trajanje < zatvaraPeriod) spojeni.push({ ...p, nepotpun: true });
        else if (p.tip === 'ODMOR' && trajanje < zatvaraPeriod && prethodni && prethodni.tip === 'RAD') prethodni.do = p.do;
        else if (prethodni && prethodni.tip === p.tip) {
            prethodni.do = p.do;
            prethodni.voznja += p.voznja;
            prethodni.rad += p.rad;
            prethodni.raspolozivost += p.raspolozivost;
        } else spojeni.push({ ...p });
    });
    if (spojeni.length) {
        if (spojeni[0].tip === 'ODMOR') spojeni[0].nepotpun = true;
        const z = spojeni[spojeni.length - 1];
        if (z.tip === 'ODMOR') z.nepotpun = true;
    }
    return spojeni.map((p) => ({ ...p, trajanje: p.do - p.od }));
}

/** UDVOJENA POSADA: interval je "u posadi" kad je bar polovina aktivnog vremena (vožnja, rad, raspoloživost) u posadi.
    U posadi (kao kolege na produkciji): pauza 45 min posle 4h30 vožnje i pauza u toku rada se NE kažnjavaju (drugi vozač vozi),
    a dnevni odmor od 9h mora biti u roku od 30h (čl. 8 st. 5 — posada), ne 24h. */
const posadaUdeo = (ctx, od, doo) => {
    if (!ctx.posada || !ctx.posada.length) return 0;
    let aktivno = 0, uPosadi = 0;
    for (const a of ctx.akt) {
        if (a.do <= od || a.od >= doo || jeOdmor(a.v)) continue; // eslint-disable-line no-continue
        const x0 = Math.max(od, a.od), x1 = Math.min(doo, a.do);
        aktivno += x1 - x0;
        for (const p of ctx.posada) uPosadi += preklop(x0, x1, p.od, p.do);
    }
    return aktivno ? uPosadi / aktivno : 0;
};
const uPosadi = (ctx, od, doo) => posadaUdeo(ctx, od, doo) >= 0.5;

const segmenti = (akt, od, doo) => akt
    .map((a) => ({ v: a.v, od: Math.max(od, a.od), do: Math.min(doo, a.do) }))
    .filter((x) => x.do > x.od)
    .map((x) => ({ ...x, t: x.do - x.od }));

/** najduži neprekidan odmor u [od, doo) — delovi na ivici se odsecaju na prozor */
const najduziOdmorU = (akt, od, doo) => {
    let naj = 0, poc = null, kraj = null;
    segmenti(akt, od, doo).forEach((a) => {
        if (!jeOdmor(a.v)) { poc = null; return; }
        if (poc === null || a.od > kraj) poc = a.od;
        kraj = a.do;
        naj = Math.max(naj, kraj - poc);
    });
    return naj;
};

/* ── pravila: dnevni odmor (čl. 8 st. 2 i 5) — AETR i gradski ─────────────── */
function praviloDnevniOdmor(ctx, L, kljucNedovoljan, kljucBroj) {
    const { periodi } = ctx;
    let skracenih = 0;
    periodi.forEach((p, i) => {
        if (p.tip === 'ODMOR') {
            if (p.trajanje >= L.nedeljniOdmorSkraceni) skracenih = 0;
            else if (!p.nepotpun && p.trajanje >= L.dnevniOdmorSkraceni && p.trajanje < L.dnevniOdmor) {
                skracenih += 1;
                if (skracenih > L.skracenihIzmedjuNedeljnih) {
                    const st = stepenDnevniOdmor(p.trajanje, false);
                    if (st) ctx.prekrsaj(kljucBroj, 'Broj skraćenih', st, p.od, p.do,
                        `Prekoračen broj skraćenih dnevnih odmora (najviše ${L.skracenihIzmedjuNedeljnih} između dva nedeljna, ovo je ${skracenih}.) — potreban pun odmor od ${fmtTrajanje(L.dnevniOdmor)}. `
                        + `Odmor ${fmtDV(p.od)} – ${fmtDV(p.do)} trajao ${fmtTrajanje(p.trajanje)}.`,
                        { trajanje: p.trajanje, manjak: L.dnevniOdmor - p.trajanje });
                }
            }
            return;
        }
        const sledeci = periodi[i + 1];
        const poslednji = i === periodi.length - 1 || (sledeci && sledeci.nepotpun);
        if (poslednji) return;
        const dozvoljenSkraceni = skracenih < L.skracenihIzmedjuNedeljnih;
        /* posada: dnevni odmor od najmanje 9h u roku od 30h od kraja prethodnog odmora */
        const posada = uPosadi(ctx, p.od, p.do);
        const prozor = posada ? 30 * H : DAN;
        const najduzi = posada ? prozor - L.dnevniOdmorSkraceni : L.najduziRadniPeriod;
        if (p.trajanje > najduzi) {
            const rok = p.od + najduzi;
            /* odmor = deo odmora posle perioda koji stane u prozor (kolege: Grikom "05:22h"); period duži od prozora bio je uvek
               "00:00h" — tada najduži neprekidan odmor u prozoru od početka perioda (Trans-Jug Niš, Velebit 15–16.10: 7:40) */
            const uProzoru = p.trajanje < prozor ? prozor - p.trajanje : najduziOdmorU(ctx.akt, p.od, p.od + prozor);
            const st = stepenDnevniOdmor(uProzoru, posada || dozvoljenSkraceni);
            if (st) ctx.prekrsaj(kljucNedovoljan, posada ? 'Nije u 30h' : 'Nije u 24h', st, p.od, p.do,
                `Dnevni odmor nije završen u roku od ${posada ? '30h (udvojena posada)' : '24h'}: radni period ${fmtDV(p.od)} – ${fmtDV(p.do)} trajao ${fmtTrajanje(p.trajanje)}, `
                + `odmor je morao početi najkasnije ${fmtDV(rok)}; u ${posada ? 30 : 24}h prozor stalo je ${fmtTrajanje(uProzoru)} odmora.`,
                { trajanje: p.trajanje, prekoracenje: p.do - rok, odmor: uProzoru, potrebno: posada || dozvoljenSkraceni ? L.dnevniOdmorSkraceni : L.dnevniOdmor });
            return;
        }
        if (sledeci && sledeci.tip === 'ODMOR' && sledeci.trajanje < L.dnevniOdmorSkraceni) {
            /* kratak odmor je samo prekid rada kad dovoljan odmor (>= skraćeni) počne na vreme — u 24h od početka perioda
               stane ceo odmor (Hewa 27–28.09: 38 min rada, 7:16 odmora, 30 min rada, pa 32h odmora — kolege: nije kazna) */
            const dovoljan = periodi.slice(i + 1).find((x) => x.tip === 'ODMOR' && x.trajanje >= L.dnevniOdmorSkraceni);
            if (dovoljan && dovoljan.od - p.od <= najduzi) return;
            const st = stepenDnevniOdmor(sledeci.trajanje, dozvoljenSkraceni);
            if (st) ctx.prekrsaj(kljucNedovoljan, 'Nedovoljan', st, sledeci.od, sledeci.do,
                `Nedovoljan dnevni odmor (najmanje ${fmtTrajanje(L.dnevniOdmor)}, skraćeni ${fmtTrajanje(L.dnevniOdmorSkraceni)}): `
                + `odmor ${fmtDV(sledeci.od)} – ${fmtDV(sledeci.do)} trajao ${fmtTrajanje(sledeci.trajanje)}.`,
                { trajanje: sledeci.trajanje, manjak: (dozvoljenSkraceni ? L.dnevniOdmorSkraceni : L.dnevniOdmor) - sledeci.trajanje });
        }
    });
}

/* ── AETR: dnevna vožnja 9h/10h (čl. 5 st. 1) ─────────────────────────────── */
function praviloDnevnaVoznjaAetr(ctx, L) {
    const produzenih = {};
    ctx.periodi.filter((p) => p.tip === 'RAD' && p.voznja > L.dnevnaVoznja).forEach((p) => {
        const k = nedeljaKljuc(p.od);
        produzenih[k] = (produzenih[k] || 0) + 1;
        if (p.voznja > L.dnevnaVoznjaProduzena) {
            const st = stepenDnevnaVoznja(p.voznja - L.dnevnaVoznjaProduzena, L.dnevnaVoznjaProduzena);
            if (st) ctx.prekrsaj('dnevnaVoznja', 'Dnevno', st, p.od, p.do,
                `Prekoračeno dnevno vreme upravljanja (najviše ${fmtTrajanje(L.dnevnaVoznja)}, izuzetno ${fmtTrajanje(L.dnevnaVoznjaProduzena)} dva puta nedeljno): vožnja ${fmtTrajanje(p.voznja)}.`,
                { trajanje: p.voznja, prekoracenje: p.voznja - L.dnevnaVoznjaProduzena });
        } else if (produzenih[k] > L.produzenihNedeljno) {
            const st = stepenDnevnaVoznja(p.voznja - L.dnevnaVoznja, L.dnevnaVoznja);
            if (st) ctx.prekrsaj('produzeniDani', 'Broj produženih', st, p.od, p.do,
                `Prekoračen broj produženih dana upravljanja (do ${fmtTrajanje(L.dnevnaVoznjaProduzena)} najviše ${L.produzenihNedeljno} puta nedeljno, ovo je ${produzenih[k]}. put): vožnja ${fmtTrajanje(p.voznja)}.`,
                { trajanje: p.voznja, prekoracenje: p.voznja - L.dnevnaVoznja });
        }
    });
}

/* ── gradski: kvar tahografa = neprekidna vožnja bez ijednog minuta druge aktivnosti (L.kvarVoznja) —
   u gradskom prevozu nemoguće (stajališta); obeležava se umesto kazne za vožnju, po izboru korisnika ── */
function kvaroviTahografa(akt, min) {
    const kvarovi = [];
    let tek = null;
    for (const a of akt) {
        if (jeVoznja(a.v) && tek && a.od <= tek.do) tek.do = Math.max(tek.do, a.do);
        else {
            if (tek && tek.do - tek.od >= min) kvarovi.push(tek);
            tek = jeVoznja(a.v) ? { od: a.od, do: a.do } : null;
        }
    }
    if (tek && tek.do - tek.od >= min) kvarovi.push(tek);
    return kvarovi;
}

/* kvar tahografa = JEDAN ceo RADNI dan (pon–pet) bez podataka na kartici (napomena NEMA_PODATAKA) između dva dana sa
   vožnjom — vozač je radio, a dan nije ubeležen. Subota/nedelja bez kartice i duži nizovi (odsustvo) su slobodni dani:
   na 8 izveštaja produkcije bi to bilo 59 lažnih "kvarova" vikendom. */
function daniBezPodataka(akt) {
    const bez = new Map(); const voznja = new Set();
    for (const a of akt) {
        const d = ponoc(a.od);
        bez.set(d, (bez.get(d) ?? true) && a.note === NEMA_PODATAKA);
        if (jeVoznja(a.v)) voznja.add(d);
    }
    const pre = (d) => d - DAN;
    return [...bez.entries()]
        .filter(([d, b]) => b && danUNedelji(d) % 6 !== 0 && voznja.has(pre(d)) && voznja.has(sledeciDan(d)))
        .map(([d]) => ({ od: d, do: sledeciDan(d) }));
}

function praviloKvarTahografa(ctx) {
    ctx.kvarovi.forEach((k) => ctx.prekrsaj('g_kvar', 'Kvar tahografa', { nivo: null, tacka: null, bezClana: true }, k.od, k.do,
        `Kvar tahografa: neprekidna vožnja ${fmtDV(k.od)} – ${fmtDV(k.do)} (${fmtTrajanje(k.do - k.od)}) bez ijednog minuta druge aktivnosti — `
        + 'umesto kazne za vožnju.', { trajanje: k.do - k.od }));
    /* dan bez podataka na kartici = vozač nije ubeležio aktivnost -> kvar tahografa (po izboru korisnika), bez kazne */
    ctx.bezPodataka.forEach((k) => ctx.prekrsaj('g_kvar', 'Nema podataka', { nivo: null, tacka: null, bezClana: true }, k.od, k.do,
        `Kvar tahografa: nema podataka na kartici ${fmtDatum(k.od)}${k.do - k.od > DAN ? ` – ${fmtDatum(k.do - 1)}` : ''} (ceo dan) — aktivnost nije ubeležena.`,
        { trajanje: k.do - k.od }));
}

/* ── gradski: dnevna vožnja 8h, bez produženja (čl. 5 st. 4) ──────────────── */
function praviloDnevnaVoznjaGradski(ctx, L) {
    ctx.periodi.filter((p) => p.tip === 'RAD' && p.voznja > L.dnevnaVoznja).forEach((p) => {
        if (ctx.kvarovi.some((k) => preklop(p.od, p.do, k.od, k.do) > 0)) return; // kvar tahografa umesto kazne
        const st = stepenDnevnaVoznja(p.voznja - L.dnevnaVoznja, L.dnevnaVoznja);
        if (st) ctx.prekrsaj('g_dnevnaVoznja', 'Dnevno (8h)', st, p.od, p.do,
            `Prekoračeno dnevno vreme upravljanja u gradskom prevozu (najviše ${fmtTrajanje(L.dnevnaVoznja)}): vožnja ${fmtTrajanje(p.voznja)}.`,
            { trajanje: p.voznja, prekoracenje: p.voznja - L.dnevnaVoznja });
    });
}

/* ── nedeljna (56h, samo AETR) i dvonedeljna (90h) vožnja (čl. 5 st. 2 i 3) ─ */
function praviloNedeljnaVoznja(ctx, L, kljucNedeljna, kljucDvonedeljna) {
    const poNedelji = new Map();
    for (const a of ctx.akt) {
        if (!jeVoznja(a.v)) continue;
        /* vožnja preko ponoći nedelje se deli na dve nedelje */
        for (let t = a.od; t < a.do;) {
            const k = nedeljaKljuc(t);
            const kraj = Math.min(a.do, plusNedelja(k));
            poNedelji.set(k, (poNedelji.get(k) || 0) + (kraj - t));
            t = kraj;
        }
    }
    const kljucevi = [...poNedelji.keys()].sort((a, b) => a - b);
    kljucevi.forEach((k, i) => {
        const v = poNedelji.get(k);
        const krajNedelje = plusNedelja(k);
        if (kljucNedeljna && v > L.nedeljnaVoznja) {
            const st = stepenNedeljnaVoznja(v);
            if (st) ctx.prekrsaj(kljucNedeljna, 'Nedeljno', st, k, krajNedelje,
                `Prekoračeno nedeljno vreme upravljanja u nedelji ${fmtDatum(k)} – ${fmtDatum(krajNedelje - 1)} (najviše ${fmtTrajanje(L.nedeljnaVoznja)}): vožnja ${fmtTrajanje(v)}.`,
                { trajanje: v, prekoracenje: v - L.nedeljnaVoznja });
        }
        const pk = kljucevi[i - 1];
        /* dve UZASTOPNE kalendarske nedelje (ne bilo koje dve susedne nedelje sa vožnjom) */
        if (pk !== undefined && plusNedelja(pk) === k) {
            const zbir = v + poNedelji.get(pk);
            if (zbir > L.dvonedeljnaVoznja) {
                const st = stepenDvonedeljnaVoznja(zbir);
                if (st) ctx.prekrsaj(kljucDvonedeljna, 'Dvonedeljno', st, pk, krajNedelje,
                    `Prekoračeno vreme upravljanja u dve uzastopne nedelje ${fmtDatum(pk)} – ${fmtDatum(krajNedelje - 1)} (najviše ${fmtTrajanje(L.dvonedeljnaVoznja)}): vožnja ${fmtTrajanje(zbir)}.`,
                    { trajanje: zbir, prekoracenje: zbir - L.dvonedeljnaVoznja });
            }
        }
    });
}

/* ── AETR: 4h30 vožnje pa pauza 45 min ili 15 + 30 (čl. 6 st. 1) ──────────── */
function praviloPauza45(ctx, L) {
    let voznja = 0, pocetak = null, prviDeo = false, pauzaUToku = 0, kraj = null;
    const zatvori = () => {
        if (voznja > L.neprekidnaVoznja && !uPosadi(ctx, pocetak, kraj)) {
            const st = stepenPeriodUpravljanja(voznja);
            /* u toleranciji (do 4h45) nema kazne; psihofizičko ga ipak beleži (kao kolege: "do 04:44h bez pauze") */
            if (st || ctx.tolerancija) ctx.prekrsaj('pauza45', st ? '4h30 bez pauze' : 'Tolerancija', st || { nivo: null, tacka: null }, pocetak, kraj,
                `Period upravljanja bez propisane pauze (posle ${fmtTrajanje(L.neprekidnaVoznja)} vožnje pauza ${fmtTrajanje(L.pauza)} ili 15 + 30 min): vožnja ${fmtTrajanje(voznja)}.`,
                { trajanje: voznja, prekoracenje: voznja - L.neprekidnaVoznja });
        }
        voznja = 0; pocetak = null; prviDeo = false; pauzaUToku = 0;
    };
    for (const a of ctx.akt) {
        const t = a.do - a.od;
        if (jeVoznja(a.v)) {
            if (pocetak === null) pocetak = a.od;
            voznja += t; kraj = a.do; pauzaUToku = 0;
        } else if (jeOdmor(a.v)) {
            pauzaUToku += t;
            if (pauzaUToku >= L.pauza || (prviDeo && pauzaUToku >= L.pauzaDrugi)) zatvori();
            else if (pauzaUToku >= L.pauzaPrvi) prviDeo = true;
        } else pauzaUToku = 0; /* rad/raspoloživost ne broje se u vožnju, ali prekidaju pauzu */
    }
    zatvori();
}

/* ── nedeljni odmori: blokovi odmora ≥24h (početak bloka je prvi odmor u bloku) ── */
function nedeljniBlokovi(periodi, min, spoj = DAN) {
    const blokovi = [];
    periodi.filter((p) => p.tip === 'ODMOR' && p.trajanje >= min).forEach((p) => {
        const b = blokovi[blokovi.length - 1];
        if (b && p.od - b.krajBloka < spoj) {
            b.krajBloka = p.do;
            if (p.trajanje > b.trajanje) { b.trajanje = p.trajanje; b.najduziOd = p.od; b.najduziDo = p.do; b.nepotpun = !!p.nepotpun; }
        } else blokovi.push({ od: p.od, najduziOd: p.od, najduziDo: p.do, trajanje: p.trajanje, krajBloka: p.do, nepotpun: !!p.nepotpun });
    });
    return blokovi;
}

function praviloKasnoNedeljni(ctx, L, blokovi, kljuc) {
    for (let i = 1; i < blokovi.length; i++) {
        const razmak = blokovi[i].od - blokovi[i - 1].krajBloka;
        if (razmak > L.nedeljniNajkasnije) {
            const rok = blokovi[i - 1].krajBloka + L.nedeljniNajkasnije;
            ctx.prekrsaj(kljuc, 'Kasno započet', stepenKasnoNedeljni(razmak - L.nedeljniNajkasnije), rok, blokovi[i].od,
                `Kasno započet nedeljni odmor (najkasnije posle šest 24-časovnih perioda): trebalo je do ${fmtDV(rok)}, započet ${fmtDV(blokovi[i].od)} — kasnio ${fmtTrajanje(razmak - L.nedeljniNajkasnije)}.`,
                { prekoracenje: razmak - L.nedeljniNajkasnije });
        }
    }
    const krajPodataka = ctx.periodi.length ? ctx.periodi[ctx.periodi.length - 1].do : null;
    const granica = Math.min(ctx.krajIzvestaja, krajPodataka ?? Infinity);
    const poslednji = blokovi[blokovi.length - 1];
    if (poslednji && Number.isFinite(granica) && granica - poslednji.krajBloka > L.nedeljniNajkasnije) {
        const rok = poslednji.krajBloka + L.nedeljniNajkasnije;
        ctx.prekrsaj(kljuc, 'Nema odmora', stepenKasnoNedeljni(granica - rok), rok, granica,
            `Nema nedeljnog odmora: rok je bio ${fmtDV(rok)}, a do ${fmtDV(granica)} nema odmora od najmanje ${fmtTrajanje(L.nedeljniOdmorSkraceni)}.`,
            { prekoracenje: granica - rok });
    }
}

/* ── AETR nedeljni odmor: dva skraćena za redom, nenadoknađen, kasno započet (čl. 8 st. 6, 7, 9) ── */
function praviloNedeljniOdmorAetr(ctx, L) {
    /* AETR: odmori prekinuti kratkim radom (do 3h — npr. premeštanje vozila) su jedan nedeljni odmor; ranije do 24h, pa su redovan
       odmor usred nedelje i skraćen preko vikenda (7h rada između) bili jedan (Grikom, Filipović 04.07) */
    const blokovi = nedeljniBlokovi(ctx.periodi, L.nedeljniOdmorSkraceni, 3 * H);
    const odmori = ctx.periodi.filter((p) => p.tip === 'ODMOR');
    /* nedeljni odmor je JEDAN po nedelji (pon–ned, po početku najdužeg odmora): slobodan dan usred nedelje (odmor >= 24h)
       nije drugi nedeljni odmor — ranije: "dva skraćena za redom" u istoj nedelji (kolege: "Nije kazna").
       Kazna "dva skraćena za redom" RESETUJE niz: sledeći skraćeni je prvi novog para (kolege, Grikom: "Nije kazna, reset je jer ima kaznu ..."). */
    const poNedelji = new Map();
    /* redovan odmor (>= 45h) se računa uvek, a od skraćenih jedan po nedelji (najduži) — redovan usred nedelje pa skraćen preko
       vikenda su dva odmora (Filipović 01–04.07 i 04–06.07) */
    const redovni = blokovi.filter((b) => b.trajanje >= L.nedeljniOdmor);
    /* od skraćenih prednost ima odmor PREKO VIKENDA (obuhvata nedelju), pa najduži (Simić 15–17.07 nije nedeljni, 18–20.07 jeste) */
    const vikend = (b) => { for (let d = ponoc(b.najduziOd); d < b.najduziDo; d = sledeciDan(d)) if (danUNedelji(d) === 0) return d; return null; };
    /* skraćen odmor usred nedelje se ne računa kad ta nedelja ima redovan odmor (Jovičić 20–22.07: slobodan dan, ne nedeljni) */
    const saRedovnim = new Set(redovni.map((b) => nedeljaKljuc(b.najduziOd)));
    blokovi.filter((b) => b.trajanje < L.nedeljniOdmor).forEach((b) => {
        const ned = vikend(b);
        if (ned === null && saRedovnim.has(nedeljaKljuc(b.najduziOd))) {
            /* ...osim kad redovan odmor te nedelje počne tek POSLE roka 6x24h od prethodnog odmora: tada je ovaj odmor usred nedelje
               bio nedeljni (SLOBUS, Đurđević: 10–11.10, rok 17.10 20:05, redovan tek 17.10 21:22 -> 15–17.10 je nedeljni) */
            const preB = blokovi[blokovi.indexOf(b) - 1];
            const redovanPosle = redovni.find((r) => r.najduziOd > b.najduziOd && nedeljaKljuc(r.najduziOd) === nedeljaKljuc(b.najduziOd));
            /* ...ili kad redovan dolazi KASNIJE u nedelji, a od prethodnog odmora je prošlo >= 24h rada — tada ovaj odmor nije produžetak
               vikenda nego nedeljni (SLOBUS, Zlatković: 02–04.10, pa 05–06.10 27:47, redovan tek 09.10 -> kazna vezana za 02–04.10);
               slobodan dan odmah posle vikend odmora (Jovičić 20–22.07, 10h rada između) i dalje nije nedeljni */
            const dovoljnoRada = preB && b.najduziOd - preB.krajBloka >= DAN;
            if (!(preB && redovanPosle && (redovanPosle.najduziOd > preB.krajBloka + 6 * DAN || dovoljnoRada))) return;
        }
        const x = { ...b, vikend: ned !== null };
        const k = nedeljaKljuc(ned !== null ? ned : b.najduziOd);
        const n = poNedelji.get(k);
        if (!n || (x.vikend && !n.vikend) || (x.vikend === n.vikend && x.trajanje > n.trajanje)) poNedelji.set(k, x);
    });
    const nedeljni = [...redovni, ...poNedelji.values()].map((b) => ({ ...b })).sort((a, b) => a.najduziOd - b.najduziOd);
    const krajPodataka = ctx.periodi.length ? ctx.periodi[ctx.periodi.length - 1].do : -Infinity;
    nedeljni.forEach((b, i) => {
        if (b.trajanje >= L.nedeljniOdmor || b.nepotpun) return;
        const manjak = L.nedeljniOdmor - b.trajanje;
        const pre = nedeljni[i - 1];
        /* skraćen odmor usred nedelje koja nije završena u podacima, a rok 6x24h od prethodnog (skraćenog) nedeljnog je posle kraja
           podataka: još nije nedeljni odmor ("nema potrebe za 45h" — SLOBUS, Vlačić 21–23.10 posle 18–20.10, kartica očitana 24.10).
           Kad rok pada u podatke, kazna ostaje (Stojanović 19–20.10). */
        if (pre && pre.trajanje < L.nedeljniOdmor && !vikend(b)
            && plusNedelja(nedeljaKljuc(b.najduziOd)) > krajPodataka
            && pre.krajBloka + 6 * DAN > krajPodataka) return;
        if (pre && !pre.nepotpun && pre.trajanje < L.nedeljniOdmor && !pre.kaznjen) {
            b.kaznjen = true;
            /* kao inspektori: za kraći odmor usred nedelje gleda se 12 dana unazad — ako tamo postoji duži skraćeni odmor u blažoj
               kategoriji (36–42h / 42–45h), kazna se vezuje za njega (SLOBUS, Đurđević: 15–17.10 35:36 -> 10–11.10 41:16) */
            const kategorija = (t) => (t >= 42 * H ? 2 : t >= 36 * H ? 1 : 0);
            let v = b;
            if (!vikend(b)) {
                blokovi.forEach((x) => {
                    if (x.najduziOd >= b.najduziOd - 12 * DAN && x.najduziOd < b.najduziOd && x.trajanje > v.trajanje
                        && x.trajanje < L.nedeljniOdmor && kategorija(x.trajanje) > kategorija(b.trajanje)) v = x;
                });
            }
            /* kazna vezana za odmor IZVAN para (12 dana pre) ne resetuje niz: drugi odmor para ostaje prvi sledećeg para
               (SLOBUS, Veljković: 20–22.09 + 22–23.09 -> kazna na 15–17.09; 22–23.09 + 29–30.09 je nova kazna 30.09) */
            if (v !== b && v.od !== pre.od) b.kaznjen = false;
            ctx.prekrsaj('dvaSkracenaNedeljna', 'Dva skraćena za redom', { nivo: 2, tacka: '55 t. 21' }, v.najduziOd, v.najduziDo,
                `Dva skraćena nedeljna odmora za redom: ${fmtDV(pre.najduziOd)} (${fmtTrajanje(pre.trajanje)}) i ${fmtDV(b.najduziOd)} (${fmtTrajanje(b.trajanje)}).`
                + (v !== b ? ` Kazna se vezuje za duži odmor u 12 dana pre: ${fmtDV(v.najduziOd)} (${fmtTrajanje(v.trajanje)}).` : ''),
                { trajanje: v.trajanje, manjak: L.nedeljniOdmor - v.trajanje });
            return;
        }
        /* nadoknada u celini uz odmor ≥9h do kraja treće naredne nedelje (čl. 8 st. 9) */
        const nedeljaOdmora = nedeljaKljuc(b.najduziOd);
        const rok = nedeljaOdmora + 4 * 7 * DAN;
        const nadoknadjen = odmori.some((o) => o.od > b.najduziDo && o.od < rok && o.trajanje >= L.dnevniOdmorSkraceni + manjak);
        if (!nadoknadjen && ctx.krajIzvestaja >= rok) {
            ctx.prekrsaj('nenadoknadjen', 'Nenadoknađen', { nivo: 2, tacka: '55 t. 28' }, b.najduziOd, b.najduziDo,
                `Nenadoknađen skraćen nedeljni odmor ${fmtDV(b.najduziOd)} – ${fmtDV(b.najduziDo)} (${fmtTrajanje(b.trajanje)}): nadoknada ${fmtTrajanje(manjak)} nije uzeta do ${fmtDatum(rok - 1)}.`,
                { trajanje: b.trajanje, manjak });
        }
    });
    /* posle SKRAĆENOG nedeljnog nema nijednog odmora >= 24h u roku 6x24h: sledeći nedeljni je "propušten" (kraći od 24h), pa su to
       dva skraćena za redom — kazna se, kao kod inspektora, vezuje za najduži skraćeni odmor u 12 dana pre roka
       (SLOBUS, Zeljković: 10–11.10 41:16, rok 17.10 20:05, redovan tek 17.10 21:22). Kasno započet ostaje poseban prekršaj. */
    for (let i = 1; i < blokovi.length; i++) {
        const pre = blokovi[i - 1];
        const rok = pre.krajBloka + L.nedeljniNajkasnije;
        if (blokovi[i].od <= rok || pre.nepotpun || pre.trajanje >= L.nedeljniOdmor) continue;
        const uNizu = nedeljni.find((b) => b.od === pre.od);
        if (!uNizu || uNizu.kaznjen) continue;
        let v = null;
        blokovi.forEach((x) => {
            if (x.najduziOd >= rok - 12 * DAN && x.najduziOd < rok && x.trajanje < L.nedeljniOdmor && (!v || x.trajanje > v.trajanje)) v = x;
        });
        if (!v) continue;
        uNizu.kaznjen = true;
        ctx.prekrsaj('dvaSkracenaNedeljna', 'Dva skraćena za redom', { nivo: 2, tacka: '55 t. 21' }, v.najduziOd, v.najduziDo,
            `Dva skraćena nedeljna odmora za redom: posle ${fmtDV(pre.najduziOd)} (${fmtTrajanje(pre.trajanje)}) nema nedeljnog odmora do ${fmtDV(rok)} `
            + `(sledeći ${fmtDV(blokovi[i].od)}). Kazna se vezuje za najduži skraćeni odmor u 12 dana pre: ${fmtDV(v.najduziOd)} (${fmtTrajanje(v.trajanje)}).`,
            { trajanje: v.trajanje, manjak: L.nedeljniOdmor - v.trajanje });
    }
    praviloKasnoNedeljni(ctx, L, blokovi, 'kasnoNedeljni');
}

/* ── gradski nedeljni odmor: najmanje skraćeni nedeljni (24h) + dnevni odmor (čl. 8 st. 10) ── */
function praviloNedeljniOdmorGradski(ctx, L) {
    /* odmori se spajaju samo preko kratkog rada (do 3h, kao AETR) — ranije do 24h, pa su vikend odmor 03–05.10 (38h) i odmor
       05–07.10 posle 6h rada bili jedan blok u drugoj nedelji, a vikend je ostao "bez odmora" (B&B, Rathnayaka 29.09–01.10) */
    const blokovi = nedeljniBlokovi(ctx.periodi, L.nedeljniOdmorSkraceni, 3 * H);
    /* ocena po nedelji ide po SVAKOM odmoru >= 24h, ne po bloku: blok predstavlja najduži deo, pa je vikend odmor 32h
       pa 1:42 rada u ponedeljak pa 70h odmora bio "70h u sledećoj nedelji", a nedelja ostala neocenjena (M&M, Vuković 17–19.10) */
    const delovi = nedeljniBlokovi(ctx.periodi, L.nedeljniOdmorSkraceni, 0);
    const potrebno = L.nedeljniOdmorSkraceni + L.dnevniOdmorSkraceni; // 24h + 9h = 33h (kao TPS)
    /* nedeljni odmor je jedan po nedelji (pon–ned). Kao kolege na produkciji: nedeljni je odmor PREKO VIKENDA
       (obuhvata nedelju) — slobodan dan od 24h usred nedelje nije nedeljni odmor. Ali odmor od 33h+ usred nedelje JESTE
       nedeljni odmor, pa kratak vikend odmor tada nije prekršaj (B&B, Pelige 06–09.10 64h, vikend 11–12.10 25h).
       Ako u nedelji nijedan odmor ne obuhvata nedelju, proverava se najduži odmor te nedelje (po početku). */
    const obuhvataNedelju = (b) => {
        for (let d = ponoc(b.najduziOd); d < b.najduziDo; d = sledeciDan(d)) if (danUNedelji(d) === 0) return d;
        return null;
    };
    const vikend = new Map(); const ostali = new Map();
    delovi.forEach((b) => {
        const ned = obuhvataNedelju(b);
        const mapa = ned !== null ? vikend : ostali;
        const k = nedeljaKljuc(ned !== null ? ned : b.najduziOd);
        const n = mapa.get(k);
        if (!n || b.trajanje > n.trajanje) mapa.set(k, b);
    });
    ostali.forEach((b, k) => {
        const v = vikend.get(k);
        if (!v || (v.trajanje < potrebno && b.trajanje >= potrebno)) vikend.set(k, b);
    });
    /* nedelja koja nije završena u podacima (kartica očitana usred nedelje) se ne ocenjuje — nedeljni odmor može tek doći */
    const krajPodataka = ctx.periodi.length ? ctx.periodi[ctx.periodi.length - 1].do : -Infinity;
    vikend.forEach((b, k) => {
        if (b.nepotpun || b.trajanje >= potrebno) return;
        if (plusNedelja(k) > krajPodataka) return;
        const manjak = potrebno - b.trajanje;
        ctx.prekrsaj('g_nedeljniOdmor', 'Kratak nedeljni', stepenGradskiNedeljni(manjak), b.najduziOd, b.najduziDo,
            `Nedeljni odmor u gradskom prevozu mora biti najmanje skraćeni nedeljni odmor (${fmtTrajanje(L.nedeljniOdmorSkraceni)}) dodat dnevnom odmoru — ukupno ${fmtTrajanje(potrebno)}: `
            + `odmor ${fmtDV(b.najduziOd)} – ${fmtDV(b.najduziDo)} trajao ${fmtTrajanje(b.trajanje)}, manjak ${fmtTrajanje(manjak)}.`,
            { trajanje: b.trajanje, manjak });
    });
    praviloKasnoNedeljni(ctx, L, blokovi, 'g_kasnoNedeljni');
}

/* ── radno vreme 14h / 10h uz noćni rad (čl. 4 st. 2 i 5) ──────────────────── */
function praviloRadnoVreme(ctx, L, kljucDnevno, kljucNocno) {
    ctx.periodi.filter((p) => p.tip === 'RAD').forEach((p) => {
        const seg = segmenti(ctx.akt, p.od, p.do);
        /* bez ključa za noćni rad (gradski, po izboru korisnika) važi samo dnevni limit 14h */
        const nocni = !kljucNocno ? 0
            : seg.filter((s) => jeVoznja(s.v) || jeRad(s.v)).reduce((a, s) => a + nocniPreklop(s.od, s.do, L.nocnoOdSat, L.nocnoDoSat), 0);
        const ukupno = seg.reduce((a, s) => a + ((jeVoznja(s.v) || jeRad(s.v) || (jeOdmor(s.v) && s.t < L.kratkaPauza)) ? s.t : 0), 0);
        const limit = nocni > 0 ? L.radnoVremeNocno : L.radnoVremeMax;
        /* uz noćni rad (10h) radno vreme je samo vožnja i ostali rad — kratke pauze se ne računaju (kao kolege na produkciji) */
        const bezPauza = seg.reduce((a, s) => a + ((jeVoznja(s.v) || jeRad(s.v)) ? s.t : 0), 0);
        if (nocni > 0 ? bezPauza <= limit : ukupno <= limit) return;
        if (nocni > 0) ctx.prekrsaj(kljucNocno, 'Noćni rad (10h)', stepenNocniRad(bezPauza), p.od, p.do,
            `Prekoračeno radno vreme uz noćni rad (najviše ${fmtTrajanje(L.radnoVremeNocno)} u 24h; noćni rad ${fmtTrajanje(nocni)} u periodu ${p2(L.nocnoOdSat)}:00–${p2(L.nocnoDoSat)}:00): radno vreme ${fmtTrajanje(bezPauza)}.`,
            { trajanje: bezPauza, prekoracenje: bezPauza - limit });
        else ctx.prekrsaj(kljucDnevno, 'Dnevno (14h)', stepenRadnoVreme(ukupno), p.od, p.do,
            `Prekoračeno dnevno radno vreme (najviše ${fmtTrajanje(L.radnoVremeMax)} u 24h; pauze kraće od 15 min ulaze u radno vreme): radno vreme ${fmtTrajanje(ukupno)}.`,
            { trajanje: ukupno, prekoracenje: ukupno - limit });
    });
}

/* ── odmor u toku dnevnog rada 30/45/60 min (čl. 7 st. 2–4), delovi od najmanje 15 min ── */
function praviloPauzeURadu(ctx, L, kljuc) {
    /* radni period se deli na BLOKOVE na odmoru od najmanje 45 min (posle pune pauze brojanje kreće iznova — kolege, Grikom:
       posle pauze 1h52 radio 10h uz 23 min pauze = "U 9h rada 23min pauze"); svaki blok se proverava kao radni dan */
    const blokovi = [];
    ctx.periodi.filter((p) => p.tip === 'RAD').forEach((p) => {
        let od = p.od, odmorOd = null;
        const zatvori = (kraj) => {
            if (odmorOd !== null && kraj - odmorOd >= 45 * MIN) { if (odmorOd > od) blokovi.push({ od, do: odmorOd }); od = kraj; }
            odmorOd = null;
        };
        for (const s of segmenti(ctx.akt, p.od, p.do)) {
            if (jeOdmor(s.v)) { if (odmorOd === null) odmorOd = s.od; } else zatvori(s.od);
        }
        zatvori(p.do);
        if (p.do > od) blokovi.push({ od, do: p.do });
    });
    blokovi.forEach((p) => {
        if (uPosadi(ctx, p.od, p.do)) return; // udvojena posada: odmara se dok drugi vozač vozi
        /* pauza od 30+ min pre prvog sata rada (odmah na početku smene) se ne računa u pauzu u toku rada — kolege (Grikom U130: 40 min
           posle 8 min rada, U137: 30 min posle 32 min rada = "imao je 10–19 / 20–29 min"); kraća se računa (U77: 26 min posle 7 min) */
        let rad0 = 0;
        const seg = segmenti(ctx.akt, p.od, p.do).filter((s) => {
            if (jeVoznja(s.v) || jeRad(s.v)) { rad0 += s.t; return true; }
            return !(jeOdmor(s.v) && s.t >= 30 * MIN && rad0 < H);
        });
        const radno = seg.reduce((a, s) => a + ((jeVoznja(s.v) || jeRad(s.v) || (jeOdmor(s.v) && s.t < L.kratkaPauza)) ? s.t : 0), 0);
        const pauza = seg.reduce((a, s) => a + ((jeOdmor(s.v) && s.t >= L.kratkaPauza) ? s.t : 0), 0);
        /* pauza je potrebna tek kad ČISTA vožnja + ostali rad pređe 6h (kolege, Grikom: 6h04 sa 29 min kratkih pauza = 5h35 rada,
           "Nije kazna"); merenje pauze ostaje (delovi < 15 min se ne računaju u pauzu) */
        const cisto = seg.reduce((a, s) => a + ((jeVoznja(s.v) || jeRad(s.v)) ? s.t : 0), 0);
        if (cisto <= 6 * H) return;
        const st = stepenPauzaURadu(radno, pauza);
        if (st) {
            ctx.prekrsaj(kljuc, 'Pauza u toku rada', st, p.od, p.do,
                `Nedovoljan odmor u toku dnevnog rada: radno vreme ${fmtTrajanje(radno)} zahteva najmanje ${Math.round(st.potrebno / MIN)} min odmora (delovi od najmanje 15 min), iskorišćeno ${Math.round(pauza / MIN)} min.`,
                { trajanje: radno, manjak: st.potrebno - pauza });
            return;
        }
        /* prvih 6h rada (kao kolege: "U 6h rada imao je ... umesto 30min"): do trenutka kad radno vreme dostigne 6h mora biti
           najmanje 30 min odmora u delovima od najmanje 15 min — ukupna pauza dana kasnije ne pokriva prvih 6h */
        if (radno <= 6 * H) return;
        let r6 = 0, pauza6 = 0, kraj6 = null;
        for (const s of seg) {
            if (jeVoznja(s.v) || jeRad(s.v) || (jeOdmor(s.v) && s.t < L.kratkaPauza)) {
                if (r6 + s.t > 6 * H) { kraj6 = s.od + (6 * H - r6); break; }
                r6 += s.t;
            } else if (jeOdmor(s.v)) pauza6 += s.t;
            if (pauza6 >= 30 * MIN) return;
        }
        if (kraj6 === null || pauza6 >= 30 * MIN) return;
        const st6 = stepenPauzaURadu(6 * H + MIN, pauza6);
        if (st6) ctx.prekrsaj(kljuc, 'Pauza u prvih 6h', st6, p.od, kraj6,
            `Nedovoljan odmor u prvih 6h rada: do ${fmtDV(kraj6)} (6h rada) najmanje 30 min odmora u delovima od najmanje 15 min, iskorišćeno ${Math.round(pauza6 / MIN)} min.`,
            { trajanje: 6 * H + MIN, manjak: 30 * MIN - pauza6 });
    });
}

/* ── noćna vožnja > 3h u 00–04 (nivo 3 po izboru korisnika; Zakon nema ovo pravilo) ───── */
function praviloNocnaVoznja(ctx, L) {
    const poDanu = new Map();
    for (const a of ctx.akt) {
        if (!jeVoznja(a.v)) continue;
        for (let dan = ponoc(a.od); dan < a.do + DAN; dan = sledeciDan(dan)) {
            const [n1, n2] = nocniProzor(dan, L.nocnoOdSat, L.nocnoDoSat);
            const x = preklop(a.od, a.do, n1, n2);
            if (x > 0) poDanu.set(dan, (poDanu.get(dan) || 0) + x);
        }
    }
    poDanu.forEach((v, dan) => {
        if (v > L.nocnaVoznjaMax) ctx.prekrsaj('nocnaVoznja', '00:00–04:00', { nivo: 3, tacka: null, bezClana: true }, ...nocniProzor(dan, L.nocnoOdSat, L.nocnoDoSat),
            `Prekoračena noćna vožnja (najviše ${fmtTrajanje(L.nocnaVoznjaMax)} u periodu ${p2(L.nocnoOdSat)}:00–${p2(L.nocnoDoSat)}:00): ${fmtTrajanje(v)} dana ${fmtDatum(dan)}.`,
            { trajanje: v, prekoracenje: v - L.nocnaVoznjaMax });
    });
}

/* ── nepoznata aktivnost / povezan ostali rad > 5h (nivo 2) ───────── */
function praviloNepoznato(ctx, L, kljuc) {
    let nep = null, rad = null;
    /* nepoznato odmah posle kvara tahografa je deo kvara */
    const posleKvara = (od) => (ctx.kvarovi || []).some((k) => Math.abs(od - k.do) <= MIN);
    const zatvoriNep = () => {
        /* nepoznato do kraja podataka = kartica izvađena pre očitavanja (sledeće umetanje nije u fajlu) — nije prekršaj;
           duže od 48h = odsustvo (godišnji, bolovanje, dani bez vožnje), ne nepovezana pauza */
        if (nep && nep.t > L.nepoznatoMax && nep.t <= 48 * H && !posleKvara(nep.od) && nep.do < ctx.krajPodataka) ctx.prekrsaj(kljuc, 'Nepoznata aktivnost', { nivo: 2, tacka: null, bezClana: true }, nep.od, nep.do,
            `Nepoznata aktivnost (nema podataka na kartici) ${fmtDV(nep.od)} – ${fmtDV(nep.do)}: ${fmtTrajanje(nep.t)} — loše upravljanje tahografom.`, { trajanje: nep.t });
        nep = null;
    };
    const zatvoriRad = () => {
        if (rad && rad.t > L.povezanRadMax && rad.do - rad.od > L.povezanRadMax) ctx.prekrsaj(kljuc, 'Povezan ostali rad', { nivo: 2, tacka: null, bezClana: true }, rad.od, rad.do,
            `Povezan ostali rad bez prekida ${fmtDV(rad.od)} – ${fmtDV(rad.do)}: ${fmtTrajanje(rad.t)} — loše upravljanje tahografom.`, { trajanje: rad.t });
        rad = null;
    };
    /* gradski: dan bez podataka je već "Kvar tahografa" — ne kažnjava se još i kao nepoznata aktivnost */
    const uDanuBezPodataka = (a) => (ctx.bezPodataka || []).some((k) => a.od >= k.od && a.do <= k.do);
    for (const a of ctx.akt) {
        const t = a.do - a.od;
        if (uDanuBezPodataka(a)) { zatvoriNep(); zatvoriRad(); continue; }
        /* samo nepoznata aktivnost sa kartice (izvađena bez ručnog unosa); dan bez podataka na kartici (dopunjen odmor) nije
           nepovezana pauza — kolege (Grikom) ne označavaju slobodne dane bez kartice */
        if (a.v === 'unknown') {
            zatvoriRad();
            if (nep && a.od <= nep.do) { nep.do = a.do; nep.t += t; } else { zatvoriNep(); nep = { od: a.od, do: a.do, t }; }
        } else if (jeRad(a.v)) {
            zatvoriNep();
            if (rad && a.od <= rad.do) { rad.do = a.do; rad.t += t; } else { zatvoriRad(); rad = { od: a.od, do: a.do, t }; }
        } else { zatvoriNep(); zatvoriRad(); }
    }
    zatvoriNep(); zatvoriRad();
    /* početna zemlja (kao kolege: "... i nema početnu zemlju"): radni dan (period rada posle dnevnog odmora) mora imati unetu
       zemlju početka — zapis mesta "Begin" u ±2h od prvog rada. Proverava se samo kad za vozača postoje zapisi mesta (kartica uvezena u novoj aplikaciji). */
    if (ctx.mesta && ctx.mesta.length) {
        /* kartica čuva ograničen broj zapisa mesta — proverava se samo raspon koji zapisi pokrivaju */
        const prvo = Math.min(...ctx.mesta), poslednje = Math.max(...ctx.mesta) + 24 * H;
        ctx.periodi.forEach((p, i) => {
            if (p.tip !== 'RAD' || i === 0 || p.od < prvo - 2 * H || p.od > poslednje) return;
            /* zapisi mesta postoje samo za period kartice: bez ijednog zapisa u ±7 dana podaci nisu sa ove kartice (Obačkić jun 2026) */
            if (!ctx.mesta.some((t) => Math.abs(t - p.od) <= 7 * DAN)) return;
            if (ctx.mesta.some((t) => Math.abs(t - p.od) <= 2 * H)) return;
            ctx.prekrsaj(kljuc, 'Bez početne zemlje', { nivo: 2, tacka: null, bezClana: true }, p.od, p.od + MIN,
                `Nije uneta zemlja početka radnog dana ${fmtDV(p.od)} — loše upravljanje tahografom.`, {});
        });
    }
}

/* ── istek kartice: upravljanje posle datuma isteka (čl. 16 st. 2; vozač čl. 58 t. 8) ── */
function praviloIstekKartice(ctx, kljuc) {
    const exp = ctx.vozac.cardExpiry ? zid(ctx.vozac.cardExpiry) : null;
    if (!exp) return;
    const granica = sledeciDan(ponoc(exp));
    const voznje = ctx.akt.filter((a) => jeVoznja(a.v) && a.do > granica);
    if (!voznje.length) return;
    const od = Math.max(granica, voznje[0].od);
    ctx.prekrsaj(kljuc, 'Istek kartice', { nivo: null, tacka: null, vozac58: true }, od, voznje[voznje.length - 1].do,
        `Upravljanje vozilom sa isteklom karticom vozača (kartica važila do ${fmtDatum(exp)}): prva vožnja posle isteka ${fmtDV(od)}.`, {});
}

/* ── OUT (gradski): po danu VOŽNJA mora biti pokrivena OUT režimom (ostali rad ne mora) ── */
function praviloOut(ctx, L) {
    /* intervali OUT: od početka (1) do kraja (2); početak bez kraja traje do kraja podataka */
    const ivice = ctx.outs.filter((o) => o.type === 1 || o.type === 2).sort((a, b) => a.t - b.t);
    const intervali = [];
    let otvoren = null;
    for (const o of ivice) {
        if (o.type === 1) { if (otvoren === null) otvoren = o.t; } else if (otvoren !== null) { intervali.push([otvoren, o.t]); otvoren = null; }
    }
    if (otvoren !== null) intervali.push([otvoren, ctx.krajPodataka]);
    const poDanu = new Map();
    for (const a of ctx.akt) {
        if (!jeVoznja(a.v)) continue;
        for (let dan = ponoc(a.od); dan < a.do; dan = sledeciDan(dan)) {
            const s = Math.max(a.od, dan), e = Math.min(a.do, sledeciDan(dan));
            if (e <= s) continue;
            const pokriveno = intervali.reduce((x, [io, id]) => x + preklop(s, e, io, id), 0);
            const d = poDanu.get(dan) || { rad: 0, out: 0 };
            d.rad += e - s; d.out += pokriveno;
            poDanu.set(dan, d);
        }
    }
    const imaOutDana = (dan) => intervali.some(([io, id]) => preklop(dan, sledeciDan(dan), io, id) > 0);
    poDanu.forEach((d, dan) => {
        if (d.rad <= 0) return;
        if (!imaOutDana(dan)) {
            ctx.prekrsaj('g_out', 'Nema OUT', { nivo: null, tacka: null, bezClana: true }, dan, sledeciDan(dan),
                `Nema OUT za vreme vožnje na dan ${fmtDatum(dan)} (vožnja ${fmtTrajanje(d.rad)}).`, { trajanje: d.rad });
        } else if (d.rad - d.out > L.outTolerancija) {
            ctx.prekrsaj('g_out', 'Vožnja van OUT', { nivo: null, tacka: null, bezClana: true }, dan, sledeciDan(dan),
                `Vožnja van OUT režima dana ${fmtDatum(dan)}: vožnja ${fmtTrajanje(d.rad)}, pod OUT režimom ${fmtTrajanje(d.out)}.`,
                { trajanje: d.rad, manjak: d.rad - d.out });
        }
    });
}

/* ── javni API ────────────────────────────────────────────────────────────── */
/** `tolerancija`: beleži i odstupanja u zakonskoj toleranciji (bez kazne) — samo za psihofizičko. */
/** propisi vozača: Turistika (dbo.Driver.Turistika) -> AETR + R Srbije međugradski, inače R Srbije gradski; bez podatka — zadati */
export const rezimiVozaca = (vozac, podrazumevano) => {
    if (vozac && typeof vozac.turistika === 'boolean') return new Set(vozac.turistika ? ['AETR', 'RS_M'] : ['RS_G']);
    return podrazumevano instanceof Set ? podrazumevano : new Set(podrazumevano || ['AETR', 'RS_M']);
};

export function izracunajPrekrsaje({ drivers, activities, outs, from, to, rezimi, skriveni, tolerancija = false, posada = [], mesta = [] }) {
    const odIzvestaja = ponoc(zid(from));
    const krajIzvestaja = sledeciDan(ponoc(zid(to)));
    const sakrij = skriveni instanceof Set ? skriveni : new Set(skriveni || []);
    const rez = new Set(rezimi && rezimi.length ? rezimi : ['AETR', 'RS_M']);
    const rezultat = [];

    const aktPoVozacu = new Map();
    for (const a of activities || []) {
        const od = zid(a.start), doo = zid(a.end);
        if (!(doo > od)) continue;
        if (!aktPoVozacu.has(a.driverId)) aktPoVozacu.set(a.driverId, []);
        aktPoVozacu.get(a.driverId).push({ v: a.type, od, do: doo, note: a.note || null });
    }
    const outPoVozacu = new Map();
    for (const o of outs || []) {
        if (!outPoVozacu.has(o.driverId)) outPoVozacu.set(o.driverId, []);
        outPoVozacu.get(o.driverId).push({ type: o.type, t: zid(o.entryDate) });
    }

    const posadaPoVozacu = new Map();
    for (const p of posada || []) {
        const od = zid(p.od), doo = zid(p.do);
        if (!(doo > od)) continue;
        if (!posadaPoVozacu.has(p.driverId)) posadaPoVozacu.set(p.driverId, []);
        posadaPoVozacu.get(p.driverId).push({ od, do: doo });
    }

    const mestaPoVozacu = new Map();
    for (const m of mesta || []) {
        const t = zid(m.t);
        if (!Number.isFinite(t)) continue;
        if (!mestaPoVozacu.has(m.driverId)) mestaPoVozacu.set(m.driverId, []);
        mestaPoVozacu.get(m.driverId).push(t);
    }

    for (const vozac of drivers || []) {
        const akt = (aktPoVozacu.get(vozac.id) || []).sort((a, b) => a.od - b.od);
        if (!akt.length) continue;
        const krajPodataka = akt[akt.length - 1].do;
        const zaRezim = (rezim) => {
            const L = LIMITI[rezim];
            const ctx = {
                vozac, akt, outs: outPoVozacu.get(vozac.id) || [], krajIzvestaja, krajPodataka, tolerancija, posada: posadaPoVozacu.get(vozac.id) || [], mesta: mestaPoVozacu.get(vozac.id) || [],
                periodi: napraviPeriode(akt, L.zatvaraPeriod),
                prekrsaj: (tip, podtip, stepen, od, doo, opis, meta = {}) => {
                    if (sakrij.has(tip)) return;
                    /* prikazuje se prekršaj koji se preklapa sa periodom izveštaja (podaci se učitavaju 28 dana pre i 14 posle,
                       pa se vide i prekršaji preko granice perioda, npr. 90h u dve nedelje) */
                    if (!(doo > odIzvestaja && od < krajIzvestaja)) return;
                    const kazna = stepen && stepen.nivo ? kaznaZa(stepen) : null;
                    rezultat.push({
                        driverId: vozac.id, vozac: vozac.name, kompanija: vozac.company || '',
                        rezim, tip, tipNaziv: (tipZa(tip) || {}).naziv || tip, podtip, opis,
                        od: izZida(od), do: izZida(doo), vreme: izZida(doo),
                        trajanje: meta.trajanje ?? null, prekoracenje: meta.prekoracenje ?? null, manjak: meta.manjak ?? null,
                        odmor: meta.odmor ?? null, potrebno: meta.potrebno ?? null,
                        nivo: stepen && stepen.nivo ? stepen.nivo : null,
                        clanTacka: kazna && !stepen.bezClana ? kazna.clanTacka : (stepen && stepen.vozac58 ? 'čl. 58 t. 8' : null),
                        kazna: kazna && !stepen.bezClana ? kazna : (kazna || null),
                        napomena: stepen && stepen.bezClana
                            ? (stepen.nivo ? `Nivo ${stepen.nivo} po internom pravilu — Zakon ne propisuje ovaj prekršaj` : 'Bez kazne — interna provera, Zakon ne propisuje ovaj prekršaj')
                            : stepen && stepen.vozac58 ? 'Vozač: 80.000–150.000 RSD + 2 kaznena poena (čl. 58)' : null,
                    });
                },
            };
            return { L, ctx };
        };

        const rv = rezimiVozaca(vozac, rez);
        if (rv.has('AETR')) {
            const { L, ctx } = zaRezim('AETR');
            praviloDnevniOdmor(ctx, L, 'dnevniOdmor', 'skraceniOdmori');
            praviloDnevnaVoznjaAetr(ctx, L);
            praviloNedeljnaVoznja(ctx, L, 'nedeljnaVoznja', 'dvonedeljnaVoznja');
            praviloPauza45(ctx, L);
            praviloNedeljniOdmorAetr(ctx, L);
        }
        if (rv.has('RS_M')) {
            const { L, ctx } = zaRezim('RS_M');
            praviloRadnoVreme(ctx, L, 'radnoVreme', 'nocniRad');
            praviloNocnaVoznja(ctx, L);
            praviloPauzeURadu(ctx, L, 'pauzeURadu');
            praviloIstekKartice(ctx, 'istekKartice');
            praviloNepoznato(ctx, L, 'nepoznato');
        }
        if (rv.has('RS_G')) {
            const { L, ctx } = zaRezim('RS_G');
            ctx.kvarovi = kvaroviTahografa(akt, L.kvarVoznja);
            ctx.bezPodataka = daniBezPodataka(akt);
            praviloKvarTahografa(ctx);
            praviloDnevnaVoznjaGradski(ctx, L);
            praviloNedeljnaVoznja(ctx, L, null, 'g_dvonedeljnaVoznja');
            praviloDnevniOdmor(ctx, L, 'g_dnevniOdmor', 'g_skraceniOdmori');
            praviloNedeljniOdmorGradski(ctx, L);
            praviloRadnoVreme(ctx, L, 'g_radnoVreme', null); // noćni rad (10h) se u gradskom ne obračunava — po izboru korisnika
            /* pauze u toku rada (30/45/60 min) se u gradskom ne obračunavaju — po izboru korisnika, kao produkcija */
            praviloOut(ctx, L);
            praviloIstekKartice(ctx, 'g_istekKartice');
            praviloNepoznato(ctx, L, 'g_nepoznato');
        }
    }
    return rezultat.sort((a, b) => (a.vozac || '').localeCompare(b.vozac || '', 'sr') || a.vreme - b.vreme);
}

export { NIVOI };
